import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { 
  TrendingUp, TrendingDown, Wallet, CreditCard, Users, Package, 
  ArrowUpRight, ArrowDownRight, ChevronDown, ChevronUp, Check, Settings2, 
  RefreshCw, AlertCircle, ShoppingCart, Receipt, FileText, DollarSign
} from 'lucide-react';
import { reportsAPI, salesAPI, purchasesAPI } from '../../services/part2-api';

// ═══════════════════════════════════════════════════════════════════════════════
// GLOSSY LINE CHART - Premium Glass Style
// ═══════════════════════════════════════════════════════════════════════════════
const GlossyLineChart = ({ salesData, purchaseData, labels }) => {
  const allValues = [...salesData, ...purchaseData];
  const max = Math.max(...allValues, 1) * 1.2;
  const height = 200;
  const width = 100;
  const padding = { top: 25, right: 5, bottom: 30, left: 5 };
  
  const getY = (value) => padding.top + ((max - value) / max) * (height - padding.top - padding.bottom);
  const getX = (index) => padding.left + (index / (labels.length - 1)) * (width - padding.left - padding.right);

  const createPath = (data) => data.map((val, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(val)}`).join(' ');
  
  // Create area path for gradient fill
  const createAreaPath = (data) => {
    const linePath = data.map((val, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(val)}`).join(' ');
    return `${linePath} L ${getX(data.length - 1)} ${height - padding.bottom} L ${getX(0)} ${height - padding.bottom} Z`;
  };

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height: '200px' }}>
      <defs>
        {/* Sales gradient */}
        <linearGradient id="salesGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#a78bfa" />
          <stop offset="100%" stopColor="#c4b5fd" />
        </linearGradient>
        <linearGradient id="salesAreaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#a78bfa" stopOpacity="0.02" />
        </linearGradient>
        {/* Purchase gradient */}
        <linearGradient id="purchaseGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#5eead4" />
          <stop offset="100%" stopColor="#99f6e4" />
        </linearGradient>
        <linearGradient id="purchaseAreaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#5eead4" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#5eead4" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      
      {/* Soft grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
        <line key={i} x1={padding.left} y1={padding.top + ratio * (height - padding.top - padding.bottom)}
          x2={width - padding.right} y2={padding.top + ratio * (height - padding.top - padding.bottom)}
          stroke="#e2e8f0" strokeWidth="0.3" strokeDasharray="2,2" />
      ))}
      
      {/* Area fills */}
      <path d={createAreaPath(purchaseData)} fill="url(#purchaseAreaGradient)" />
      <path d={createAreaPath(salesData)} fill="url(#salesAreaGradient)" />
      
      {/* Lines */}
      <path d={createPath(purchaseData)} fill="none" stroke="url(#purchaseGradient)" strokeWidth="2" strokeLinecap="round" />
      <path d={createPath(salesData)} fill="none" stroke="url(#salesGradient)" strokeWidth="2" strokeLinecap="round" />
      
      {/* Glow effect dots */}
      {salesData.map((val, i) => (
        <g key={`s-${i}`}>
          <circle cx={getX(i)} cy={getY(val)} r="4" fill="#a78bfa" fillOpacity="0.2" />
          <circle cx={getX(i)} cy={getY(val)} r="2.5" fill="#fff" stroke="#a78bfa" strokeWidth="1.5" />
        </g>
      ))}
      {purchaseData.map((val, i) => (
        <g key={`p-${i}`}>
          <circle cx={getX(i)} cy={getY(val)} r="4" fill="#5eead4" fillOpacity="0.2" />
          <circle cx={getX(i)} cy={getY(val)} r="2.5" fill="#fff" stroke="#5eead4" strokeWidth="1.5" />
        </g>
      ))}
      
      {/* Labels */}
      {labels.map((label, i) => (
        <text key={i} x={getX(i)} y={height - 10} textAnchor="middle" className="text-[7px] fill-slate-400 font-light">{label}</text>
      ))}
    </svg>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CIRCULAR PROGRESS - Soft Donut for Profit
// ═══════════════════════════════════════════════════════════════════════════════
const SoftDonut = ({ percentage, size = 100, color = '#a78bfa' }) => {
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(Math.max(percentage, 0), 100) / 100) * circumference;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <defs>
          <linearGradient id="donutGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color} />
            <stop offset="100%" stopColor={color} stopOpacity="0.6" />
          </linearGradient>
        </defs>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#f1f5f9" strokeWidth={strokeWidth} />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="url(#donutGradient)" strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          className="transition-all duration-1000 ease-out" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-lg font-medium text-slate-700">{percentage.toFixed(0)}%</span>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// GLOSSY KPI CARD
// ═══════════════════════════════════════════════════════════════════════════════
const GlossyKPICard = ({ label, value, icon: Icon, gradient, trend, trendUp }) => (
  <div className={`relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br ${gradient} 
    shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 group`}>
    {/* Gloss overlay */}
    <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-white/10 to-transparent pointer-events-none" />
    
    <div className="relative z-10 flex items-start justify-between">
      <div>
        <p className="text-[11px] font-normal text-slate-600/80 mb-1">{label}</p>
        <p className="text-xl font-semibold text-slate-800">{value}</p>
        {trend && (
          <div className={`flex items-center gap-1 mt-1.5 ${trendUp ? 'text-emerald-600' : 'text-rose-500'}`}>
            {trendUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            <span className="text-[10px] font-medium">{trend}</span>
          </div>
        )}
      </div>
      <div className="w-9 h-9 rounded-xl bg-white/50 backdrop-blur-sm flex items-center justify-center 
        group-hover:scale-110 transition-transform duration-300">
        <Icon className="w-4 h-4 text-slate-600/70" />
      </div>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// GLASS CARD WRAPPER
// ═══════════════════════════════════════════════════════════════════════════════
const GlassCard = ({ children, className = '', hover = true }) => (
  <div className={`relative overflow-hidden rounded-2xl bg-white/70 backdrop-blur-sm 
    border border-white/60 shadow-[0_4px_24px_-4px_rgba(0,0,0,0.08)] 
    ${hover ? 'hover:shadow-[0_8px_32px_-4px_rgba(0,0,0,0.12)] hover:-translate-y-0.5' : ''} 
    transition-all duration-300 ${className}`}>
    {/* Subtle top gloss */}
    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white to-transparent" />
    {children}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// KPI CUSTOMIZE DROPDOWN
// ═══════════════════════════════════════════════════════════════════════════════
const KPIDropdown = ({ allKPIs, visible, onToggle }) => (
  <div className="absolute right-0 top-full mt-2 w-52 bg-white/95 backdrop-blur-xl border border-slate-200/60 
    rounded-xl shadow-xl shadow-slate-200/50 z-50 py-2 animate-fadeSlideDown">
    <p className="px-3 py-1.5 text-[10px] font-medium text-slate-400 uppercase tracking-wider">Show / Hide</p>
    {allKPIs.map((kpi) => (
      <button key={kpi.id} onClick={() => onToggle(kpi.id)}
        className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50/80 transition-colors text-left">
        <div className={`w-4 h-4 rounded-md border-2 flex items-center justify-center transition-all
          ${visible.includes(kpi.id) ? 'bg-violet-500 border-violet-500' : 'border-slate-300'}`}>
          {visible.includes(kpi.id) && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
        </div>
        <span className="text-[13px] text-slate-600">{kpi.label}</span>
      </button>
    ))}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════════
const OwnerDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const { activeBusiness } = useBusiness();
  const { businessId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showKPIDropdown, setShowKPIDropdown] = useState(false);
  const [dateRange, setDateRange] = useState('month');
  
  const [dashboardData, setDashboardData] = useState({
    todaySales: 0, monthSales: 0, monthPurchases: 0, monthExpenses: 0,
    customerDues: 0, supplierDues: 0, cashOnHand: 0, bankBalance: 0,
    lowStock: [], recentSalesInvoices: [], recentPurchaseInvoices: [],
  });

  // KPI configuration with gradients - dynamic labels based on date range
  const periodLabel = dateRange === 'today' ? 'Today' : dateRange === 'week' ? 'This Week' : 'This Month';
  const allKPIs = [
    { id: 'salesToday', label: 'Sales Today', icon: TrendingUp, gradient: 'from-violet-100/80 to-purple-50/60' },
    { id: 'salesMonth', label: `Sales ${periodLabel}`, icon: DollarSign, gradient: 'from-fuchsia-100/80 to-pink-50/60' },
    { id: 'receivables', label: 'Receivables', icon: Users, gradient: 'from-amber-100/80 to-orange-50/60' },
    { id: 'payables', label: 'Payables', icon: ShoppingCart, gradient: 'from-rose-100/80 to-red-50/60' },
    { id: 'cashBalance', label: 'Cash Balance', icon: Wallet, gradient: 'from-emerald-100/80 to-green-50/60' },
    { id: 'bankBalance', label: 'Bank Balance', icon: CreditCard, gradient: 'from-sky-100/80 to-blue-50/60' },
    { id: 'netProfit', label: 'Net Profit', icon: TrendingUp, gradient: 'from-teal-100/80 to-cyan-50/60' },
  ];

  const [visibleKPIs, setVisibleKPIs] = useState(() => {
    const saved = localStorage.getItem(`dashboard_kpis_v2_${businessId}`);
    return saved ? JSON.parse(saved) : ['salesToday', 'salesMonth', 'receivables', 'payables', 'cashBalance', 'bankBalance', 'netProfit'];
  });

  // Collapsible header state
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(() => {
    const saved = localStorage.getItem('header_collapsed_dashboard');
    return saved === 'true';
  });

  useEffect(() => {
    localStorage.setItem('header_collapsed_dashboard', String(isHeaderCollapsed));
  }, [isHeaderCollapsed]);

  useEffect(() => {
    localStorage.setItem(`dashboard_kpis_v2_${businessId}`, JSON.stringify(visibleKPIs));
  }, [visibleKPIs, businessId]);

  const toggleKPI = (id) => setVisibleKPIs(prev => prev.includes(id) ? prev.filter(k => k !== id) : [...prev, id]);

  // Role redirect
  useEffect(() => {
    if (!authLoading && user) {
      const role = user.role?.toUpperCase();
      const normalizedRole = role === 'BUSINESS_OWNER' ? 'OWNER' : role;
      if (normalizedRole === 'STAFF') {
        navigate(`/business/${businessId}/dashboard/staff`, { replace: true });
      }
    }
  }, [authLoading, user, businessId, navigate]);

  useEffect(() => { fetchDashboardData(); }, [dateRange]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const today = new Date();
      const todayDate = today.toISOString().split('T')[0];
      
      // Calculate date range based on selection
      let fromDate, toDate;
      if (dateRange === 'today') {
        fromDate = todayDate;
        toDate = todayDate;
      } else if (dateRange === 'week') {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - 7);
        fromDate = weekStart.toISOString().split('T')[0];
        toDate = todayDate;
      } else { // month -- last 30 days (rolling window, not calendar month)
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        fromDate = thirtyDaysAgo.toISOString().split('T')[0];
        toDate = todayDate;
      }

      const [todaySales, periodSales, periodPurchase, periodExpense, partyOutstanding, cashBank, lowStock, recentSales, recentPurchases] = await Promise.all([
        reportsAPI.salesSummary({ from_date: todayDate, to_date: todayDate }).catch(() => ({ data: null })),
        reportsAPI.salesSummary({ from_date: fromDate, to_date: toDate }).catch(() => ({ data: null })),
        reportsAPI.purchaseSummary({ from_date: fromDate, to_date: toDate }).catch(() => ({ data: null })),
        reportsAPI.expenseByCategory({ from_date: fromDate, to_date: toDate }).catch(() => ({ data: [] })),
        reportsAPI.partyOutstanding().catch(() => ({ data: [] })),
        reportsAPI.cashBankSummary().catch(() => ({ data: null })),
        reportsAPI.lowStock().catch(() => ({ data: [] })),
        salesAPI.listInvoices({ limit: 5 }).catch(() => ({ data: [] })),
        purchasesAPI.listInvoices({ limit: 5 }).catch(() => ({ data: [] })),
      ]);

      // Expenses: response is { rows, totals: { total_expenses } }
      const expenses = periodExpense.data?.totals?.total_expenses || 0;

      // Party outstanding: response is { receivables, payables, totals: { total_receivable, total_payable } }
      const customerDues = partyOutstanding.data?.totals?.total_receivable || 0;
      const supplierDues = partyOutstanding.data?.totals?.total_payable || 0;

      setDashboardData({
        todaySales: todaySales.data?.totals?.total_sales || 0,
        monthSales: periodSales.data?.totals?.total_sales || 0,
        monthPurchases: periodPurchase.data?.totals?.total_purchases || 0,
        monthExpenses: expenses,
        customerDues, supplierDues,
        cashOnHand: cashBank.data?.totals?.cash_in_hand || 0,
        bankBalance: cashBank.data?.totals?.bank_balance || 0,
        lowStock: lowStock.data?.rows || [],
        recentSalesInvoices: recentSales.data?.invoices || (Array.isArray(recentSales.data) ? recentSales.data : []),
        recentPurchaseInvoices: recentPurchases.data?.invoices || (Array.isArray(recentPurchases.data) ? recentPurchases.data : []),
      });
      setError(null);
    } catch (e) {
      setError('Unable to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amt) => {
    if (!amt && amt !== 0) return '₹0';
    const sign = amt < 0 ? '-' : '';
    const abs = Math.abs(amt);
    if (abs >= 10000000) return `${sign}₹${(abs / 10000000).toFixed(2)} Cr`;
    if (abs >= 100000) return `${sign}₹${(abs / 100000).toFixed(2)} L`;
    if (abs >= 1000) return `${sign}₹${(abs / 1000).toFixed(1)}K`;
    return `${sign}₹${abs.toLocaleString('en-IN')}`;
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (showKPIDropdown && !e.target.closest('.kpi-dropdown-wrap')) setShowKPIDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showKPIDropdown]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96 bg-gradient-to-br from-slate-50 via-violet-50/30 to-teal-50/30">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-violet-200 border-t-violet-500 rounded-full animate-spin" />
          <p className="text-slate-500 text-sm font-light">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Calculations
  const grossProfit = dashboardData.monthSales - dashboardData.monthPurchases;
  const netProfit = grossProfit - dashboardData.monthExpenses;
  const profitMargin = dashboardData.monthSales > 0 ? (netProfit / dashboardData.monthSales * 100) : 0;
  const totalBalance = dashboardData.cashOnHand + dashboardData.bankBalance;
  const cashInflow = dashboardData.monthSales;
  const cashOutflow = dashboardData.monthPurchases + dashboardData.monthExpenses;
  const netCashFlow = cashInflow - cashOutflow;

  // Chart data
  const chartLabels = ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
  const salesChart = [dashboardData.monthSales * 0.22, dashboardData.monthSales * 0.26, dashboardData.monthSales * 0.24, dashboardData.monthSales * 0.28];
  const purchaseChart = [dashboardData.monthPurchases * 0.20, dashboardData.monthPurchases * 0.30, dashboardData.monthPurchases * 0.25, dashboardData.monthPurchases * 0.25];

  // KPI values
  const kpiData = {
    salesToday: dashboardData.todaySales,
    salesMonth: dashboardData.monthSales,
    receivables: dashboardData.customerDues,
    payables: dashboardData.supplierDues,
    cashBalance: dashboardData.cashOnHand,
    bankBalance: dashboardData.bankBalance,
    netProfit: netProfit,
  };

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageSlideIn">
      {/* ═══════════════════════════════════════════════════════════════════════
          COLLAPSIBLE HEADER - Same as Sales Orders
          ═══════════════════════════════════════════════════════════════════════ */}
      
      {/* Collapsed State - Minimal Header */}
      {isHeaderCollapsed ? (
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-3 bg-white border-b border-slate-200 shadow-sm">
          <div className="flex items-center gap-4">
            <h1 className="text-[15px] font-medium text-slate-700">Dashboard</h1>
            <span className="text-[12px] text-slate-400">• {dateRange === 'today' ? "Today" : dateRange === 'week' ? 'This Week' : 'This Month'}</span>
          </div>
          <div className="flex items-center gap-3">
            {/* Date filter pills */}
            <div className="flex items-center bg-slate-100 rounded-lg overflow-hidden">
              {['today', 'week', 'month'].map((r) => (
                <button key={r} onClick={() => setDateRange(r)}
                  className={`px-3 py-1.5 text-[11px] font-medium transition-all ${
                    dateRange === r ? 'bg-teal-600 text-white' : 'text-slate-500 hover:text-slate-700'
                  }`}>
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </button>
              ))}
            </div>
            <button
              onClick={() => setIsHeaderCollapsed(false)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-teal-600 bg-teal-50 hover:bg-teal-100 rounded-lg transition-all border border-teal-200"
            >
              <ChevronDown className="w-3.5 h-3.5" />
              Expand
            </button>
          </div>
        </div>
      ) : (
        /* Expanded State - Full Header */
        <>
          <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm relative">
            {/* Collapse Button - Premium styled */}
            <button
              onClick={() => setIsHeaderCollapsed(true)}
              className="absolute top-3 right-6 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-teal-600 bg-teal-50 hover:bg-teal-100 rounded-lg transition-all border border-teal-200"
            >
              <ChevronUp className="w-3.5 h-3.5" />
              Collapse
            </button>
            <div className="max-w-[1600px] mx-auto px-6 py-4">
              {/* Title Row */}
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-[18px] font-semibold text-slate-800">Dashboard</h1>
                  <p className="text-[12px] text-slate-500 mt-0.5">Business performance overview</p>
                </div>
                <div className="flex items-center gap-3 mr-24">
                  {/* Date filter pills */}
                  <div className="flex items-center bg-slate-100 rounded-lg overflow-hidden">
                    {['today', 'week', 'month'].map((r) => (
                      <button key={r} onClick={() => setDateRange(r)}
                        className={`px-3 py-1.5 text-[11px] font-medium transition-all ${
                          dateRange === r ? 'bg-teal-600 text-white' : 'text-slate-500 hover:text-slate-700'
                        }`}>
                        {r.charAt(0).toUpperCase() + r.slice(1)}
                      </button>
                    ))}
                  </div>
                  
                  {/* Customize button */}
                  <div className="relative kpi-dropdown-wrap">
                    <button
                      onClick={() => setShowKPIDropdown(!showKPIDropdown)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 rounded-lg text-[11px] font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-200 transition-all"
                    >
                      <Settings2 className="w-3.5 h-3.5" />
                      Customize
                    </button>
                    {showKPIDropdown && <KPIDropdown allKPIs={allKPIs} visible={visibleKPIs} onToggle={toggleKPI} />}
                  </div>
                  
                  {/* Refresh */}
                  <button onClick={fetchDashboardData} className="p-2 bg-slate-100 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-200 transition-all">
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Stats & KPIs Section */}
          <div className="flex-shrink-0 bg-[#F8F9FB] px-6 py-4">
            <div className="max-w-[1600px] mx-auto">
              {/* KPI Stats Pills - Same style as Sales Orders */}
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {visibleKPIs.map((id) => {
                  const kpi = allKPIs.find(k => k.id === id);
                  if (!kpi) return null;
                  const val = kpiData[id];
                  const isProfit = id === 'netProfit';
                  const isNegative = val < 0;
                  
                  // Color mapping
                  const colorMap = {
                    salesToday: { bg: 'bg-violet-50', border: 'border-violet-200', icon: 'text-violet-500', value: 'text-violet-600' },
                    salesMonth: { bg: 'bg-fuchsia-50', border: 'border-fuchsia-200', icon: 'text-fuchsia-500', value: 'text-fuchsia-600' },
                    receivables: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-500', value: 'text-amber-600' },
                    payables: { bg: 'bg-rose-50', border: 'border-rose-200', icon: 'text-rose-500', value: 'text-rose-600' },
                    cashBalance: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'text-emerald-500', value: 'text-emerald-600' },
                    bankBalance: { bg: 'bg-sky-50', border: 'border-sky-200', icon: 'text-sky-500', value: 'text-sky-600' },
                    netProfit: isNegative 
                      ? { bg: 'bg-rose-50', border: 'border-rose-200', icon: 'text-rose-500', value: 'text-rose-600' }
                      : { bg: 'bg-teal-50', border: 'border-teal-200', icon: 'text-teal-500', value: 'text-teal-600' },
                  };
                  const colors = colorMap[id] || colorMap.salesToday;
                  
                  return (
                    <div key={id} className={`inline-flex items-center gap-3 px-4 py-2.5 bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200`}>
                      <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center`}>
                        <kpi.icon className={`w-4 h-4 ${colors.icon}`} />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">{kpi.label}</p>
                        <div className="flex items-center gap-1.5">
                          <p className={`text-[16px] font-bold ${colors.value}`}>{formatCurrency(val)}</p>
                          {isProfit && (
                            <span className={`text-[10px] font-medium ${profitMargin >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                              {profitMargin.toFixed(1)}%
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto px-6 pb-5">
        <div className="max-w-[1600px] mx-auto space-y-5">
          {/* Error */}
          {error && (
            <div className="bg-amber-50/80 backdrop-blur-sm border border-amber-200/60 rounded-xl px-4 py-3 flex items-center gap-3">
              <AlertCircle className="w-4 h-4 text-amber-500" />
              <p className="text-sm text-amber-700 font-light">{error}</p>
            </div>
          )}

        {/* ═══════════════════════════════════════════════════════════════════════
            ANALYTICS SECTION
            ═══════════════════════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          
          {/* Sales vs Purchases Chart */}
          <GlassCard className="lg:col-span-2 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-slate-700">Sales vs Purchases</h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-violet-400 to-purple-300" />
                  <span className="text-[11px] text-slate-400">Sales</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-teal-400 to-cyan-300" />
                  <span className="text-[11px] text-slate-400">Purchases</span>
                </div>
              </div>
            </div>
            <GlossyLineChart salesData={salesChart} purchaseData={purchaseChart} labels={chartLabels} />
          </GlassCard>

          {/* Profit Summary with Donut */}
          <GlassCard className="p-5">
            <h3 className="text-sm font-medium text-slate-700 mb-4">Profit Summary</h3>
            <div className="flex items-center gap-4">
              <SoftDonut percentage={Math.min(Math.abs(profitMargin), 100)} size={90} color={profitMargin >= 0 ? '#a78bfa' : '#f43f5e'} />
              <div className="flex-1 space-y-3">
                <div>
                  <p className="text-[11px] text-slate-400 font-light">Gross Profit</p>
                  <p className={`text-lg font-semibold ${grossProfit >= 0 ? 'text-slate-700' : 'text-rose-500'}`}>
                    {formatCurrency(grossProfit)}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] text-slate-400 font-light">Net Profit</p>
                  <p className={`text-lg font-semibold ${netProfit >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
                    {formatCurrency(netProfit)}
                  </p>
                </div>
              </div>
            </div>
          </GlassCard>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════
            CASH FLOW SUMMARY
            ═══════════════════════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Inflow */}
          <div className="relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br from-emerald-100/70 to-green-50/50
            shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-white/50 via-white/20 to-transparent pointer-events-none" />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-2">
                <ArrowUpRight className="w-4 h-4 text-emerald-600" />
                <p className="text-xs font-medium text-emerald-700/70">Cash Inflow</p>
              </div>
              <p className="text-2xl font-semibold text-emerald-800">{formatCurrency(cashInflow)}</p>
              <p className="text-[11px] text-emerald-600/60 mt-1">From sales this month</p>
            </div>
          </div>

          {/* Outflow */}
          <div className="relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br from-amber-100/70 to-orange-50/50
            shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-white/50 via-white/20 to-transparent pointer-events-none" />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-2">
                <ArrowDownRight className="w-4 h-4 text-amber-600" />
                <p className="text-xs font-medium text-amber-700/70">Cash Outflow</p>
              </div>
              <p className="text-2xl font-semibold text-amber-800">{formatCurrency(cashOutflow)}</p>
              <p className="text-[11px] text-amber-600/60 mt-1">Purchases + Expenses</p>
            </div>
          </div>

          {/* Net Flow */}
          <div className={`relative overflow-hidden rounded-2xl p-5 
            ${netCashFlow >= 0 ? 'bg-gradient-to-br from-sky-100/70 to-blue-50/50' : 'bg-gradient-to-br from-rose-100/70 to-red-50/50'}
            shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300`}>
            <div className="absolute inset-0 bg-gradient-to-br from-white/50 via-white/20 to-transparent pointer-events-none" />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-2">
                {netCashFlow >= 0 ? <TrendingUp className="w-4 h-4 text-sky-600" /> : <TrendingDown className="w-4 h-4 text-rose-600" />}
                <p className={`text-xs font-medium ${netCashFlow >= 0 ? 'text-sky-700/70' : 'text-rose-700/70'}`}>Net Cash Flow</p>
              </div>
              <p className={`text-2xl font-semibold ${netCashFlow >= 0 ? 'text-sky-800' : 'text-rose-800'}`}>
                {formatCurrency(netCashFlow)}
              </p>
              <p className={`text-[11px] mt-1 ${netCashFlow >= 0 ? 'text-sky-600/60' : 'text-rose-600/60'}`}>
                {netCashFlow >= 0 ? 'Positive cash flow' : 'Negative cash flow'}
              </p>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════
            OPERATIONAL INSIGHTS
            ═══════════════════════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Low Stock', value: dashboardData.lowStock.length, icon: Package, gradient: 'from-rose-100/60 to-pink-50/40', color: 'text-rose-700' },
            { label: 'Receivables Due', value: formatCurrency(dashboardData.customerDues), icon: Users, gradient: 'from-amber-100/60 to-yellow-50/40', color: 'text-amber-700' },
            { label: 'Payables Due', value: formatCurrency(dashboardData.supplierDues), icon: ShoppingCart, gradient: 'from-violet-100/60 to-purple-50/40', color: 'text-violet-700' },
            { label: 'Cash in Hand', value: formatCurrency(dashboardData.cashOnHand), icon: Wallet, gradient: 'from-emerald-100/60 to-green-50/40', color: 'text-emerald-700' },
            { label: 'Bank Balance', value: formatCurrency(dashboardData.bankBalance), icon: CreditCard, gradient: 'from-sky-100/60 to-blue-50/40', color: 'text-sky-700' },
          ].map((item, idx) => (
            <div key={idx} className={`relative overflow-hidden rounded-xl p-4 bg-gradient-to-br ${item.gradient}
              shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300`}>
              <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-white/10 to-transparent pointer-events-none" />
              <div className="relative z-10">
                <item.icon className="w-4 h-4 text-slate-400/60 mb-2" />
                <p className="text-[10px] text-slate-500 font-light mb-0.5">{item.label}</p>
                <p className={`text-lg font-semibold ${item.color}`}>{item.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════
            RECENT ACTIVITY
            ═══════════════════════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          
          {/* Recent Sales */}
          <GlassCard className="overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100/60">
              <h3 className="text-sm font-medium text-slate-700">Recent Sales</h3>
              <Link to={`/business/${businessId}/sales/invoices`} 
                className="text-xs text-violet-500 hover:text-violet-600 font-medium transition-colors">View all →</Link>
            </div>
            {dashboardData.recentSalesInvoices.length === 0 ? (
              <div className="px-5 py-10 text-center">
                <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400 font-light">No recent sales</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100/60">
                {dashboardData.recentSalesInvoices.slice(0, 5).map((inv, idx) => (
                  <div key={inv.id || idx} className="px-5 py-3 hover:bg-slate-50/50 transition-colors cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-700 group-hover:text-violet-600 transition-colors">{inv.invoice_number}</p>
                        <p className="text-xs text-slate-400 font-light">{inv.party_name || '---'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-700">₹{inv.grand_total?.toLocaleString('en-IN') || '0'}</p>
                        <p className={`text-[11px] font-light ${
                          inv.status === 'paid' ? 'text-emerald-500' : inv.status === 'overdue' ? 'text-rose-500' : 'text-amber-500'
                        }`}>{inv.status || 'pending'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </GlassCard>

          {/* Recent Purchases */}
          <GlassCard className="overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100/60">
              <h3 className="text-sm font-medium text-slate-700">Recent Purchases</h3>
              <Link to={`/business/${businessId}/purchases/invoices`} 
                className="text-xs text-teal-500 hover:text-teal-600 font-medium transition-colors">View all →</Link>
            </div>
            {dashboardData.recentPurchaseInvoices.length === 0 ? (
              <div className="px-5 py-10 text-center">
                <Receipt className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400 font-light">No recent purchases</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100/60">
                {dashboardData.recentPurchaseInvoices.slice(0, 5).map((inv, idx) => (
                  <div key={inv.id || idx} className="px-5 py-3 hover:bg-slate-50/50 transition-colors cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-700 group-hover:text-teal-600 transition-colors">{inv.invoice_number}</p>
                        <p className="text-xs text-slate-400 font-light">{inv.party_name || '---'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-700">₹{inv.grand_total?.toLocaleString('en-IN') || '0'}</p>
                        <p className={`text-[11px] font-light ${
                          inv.status === 'paid' ? 'text-emerald-500' : inv.status === 'overdue' ? 'text-rose-500' : 'text-amber-500'
                        }`}>{inv.status || 'pending'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </GlassCard>
        </div>

        {/* Low Stock Alert */}
        {dashboardData.lowStock.length > 0 && (
          <GlassCard className="overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100/60">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-rose-400 animate-pulse" />
                <h3 className="text-sm font-medium text-slate-700">Low Stock Alerts</h3>
              </div>
              <Link to={`/business/${businessId}/items`} 
                className="text-xs text-rose-500 hover:text-rose-600 font-medium transition-colors">Manage →</Link>
            </div>
            <div className="divide-y divide-slate-100/60">
              {dashboardData.lowStock.slice(0, 4).map((item, idx) => (
                <div key={idx} className="px-5 py-3 flex items-center justify-between hover:bg-rose-50/30 transition-colors">
                  <span className="text-sm text-slate-700">{item.item_name || item.name}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-slate-400">Min: {item.threshold || item.min_stock}</span>
                    <span className="text-sm font-medium text-rose-600">Current: {item.current_stock}</span>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        )}

        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeSlideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeSlideDown {
          animation: fadeSlideDown 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default OwnerDashboard;
